import React, { useState, useEffect } from 'react';
import { useToast } from '../../context/ToastContext';
import { Card, Button, Badge, SectionHeader, Spinner, Modal } from '../../components/shared/UI';
import API from '../../utils/apiClient';

// Simple inline rich-text editor using a contentEditable div
// (avoids react-quill dependency issues; teachers can use basic formatting)
function RichEditor({ value, onChange, placeholder = 'Write your notes here...' }) {
  return (
    <div className="border-2 border-gray-200 rounded-xl overflow-hidden focus-within:border-brand-500">
      {/* Toolbar */}
      <div className="flex items-center gap-1 p-2 bg-surface border-b border-gray-200 flex-wrap">
        {[
          { cmd:'bold',          label:<strong>B</strong> },
          { cmd:'italic',        label:<em>I</em> },
          { cmd:'underline',     label:<u>U</u> },
          { cmd:'insertUnorderedList', label:'• List' },
          { cmd:'insertOrderedList',   label:'1. List' },
          { cmd:'formatBlock',   label:'H2', value:'h2' },
          { cmd:'formatBlock',   label:'H3', value:'h3' },
          { cmd:'formatBlock',   label:'¶',  value:'p' },
        ].map((btn, i) => (
          <button key={i} type="button"
            onMouseDown={e => {
              e.preventDefault();
              document.execCommand(btn.cmd, false, btn.value || null);
            }}
            className="px-2 py-1 text-xs font-bold rounded border border-gray-200 bg-white hover:bg-gray-100 text-de-black transition-colors min-w-[28px] text-center">
            {btn.label}
          </button>
        ))}
      </div>
      {/* Editable area */}
      <div
        contentEditable
        suppressContentEditableWarning
        data-placeholder={placeholder}
        onInput={e => onChange(e.currentTarget.innerHTML)}
        dangerouslySetInnerHTML={{ __html: value }}
        className="min-h-[280px] p-4 text-sm text-de-text leading-relaxed focus:outline-none prose prose-sm max-w-none"
        style={{
          fontFamily: 'inherit',
        }}
      />
      <style>{`[contenteditable]:empty:before { content: attr(data-placeholder); color: #aaa; }`}</style>
    </div>
  );
}

const LEVELS = ['A1','A2','B1','B2'];
const emptyForm = { title:'', content:'', level:'A1', topic:'' };

export default function AdminNotesPage() {
  const { addToast } = useToast();
  const [notes, setNotes]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [tab, setTab]             = useState('list'); // 'list' | 'new' | 'edit'
  const [form, setForm]           = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving]       = useState(false);
  const [deleteModal, setDeleteModal] = useState(null);
  const [deleting, setDeleting]   = useState(false);

  const load = () => {
    API.get('/notes')
      .then(({ data }) => setNotes(data.notes || []))
      .catch(() => addToast('Failed to load notes', 'error'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const setF = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const openNew = () => {
    setForm(emptyForm);
    setEditingId(null);
    setTab('new');
  };

  const openEdit = (note) => {
    setForm({ title: note.title, content: note.content, level: note.level, topic: note.topic || '' });
    setEditingId(note.id);
    setTab('edit');
  };

  const handleSave = async () => {
    if (!form.title.trim())   { addToast('Title is required', 'error'); return; }
    if (!form.content.trim()) { addToast('Content is required', 'error'); return; }
    setSaving(true);
    try {
      if (editingId) {
        await API.put(`/notes/${editingId}`, form);
        addToast('Note updated!', 'success');
      } else {
        await API.post('/notes', form);
        addToast('Note created!', 'success');
      }
      load();
      setTab('list');
    } catch (err) {
      addToast(err.message || 'Save failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteModal) return;
    setDeleting(true);
    try {
      await API.delete(`/notes/${deleteModal.id}`);
      addToast('Note deleted', 'success');
      setNotes(p => p.filter(n => n.id !== deleteModal.id));
      setDeleteModal(null);
    } catch (err) {
      addToast(err.message || 'Delete failed', 'error');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <SectionHeader title="Study Notes" subtitle="Create digital textbook notes for students" />
        <div className="flex gap-2">
          {tab !== 'list' && (
            <Button variant="secondary" onClick={() => setTab('list')}>← Back to List</Button>
          )}
          {tab === 'list' && (
            <Button variant="primary" onClick={openNew}>+ New Note</Button>
          )}
        </div>
      </div>

      {/* List */}
      {tab === 'list' && (
        <Card className="overflow-hidden">
          {loading ? (
            <div className="flex justify-center py-10"><Spinner className="text-brand-500" /></div>
          ) : notes.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-4xl mb-3">📓</p>
              <p className="text-sm font-bold text-de-black mb-1">No notes yet</p>
              <Button variant="primary" onClick={openNew}>Create First Note</Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-surface border-b border-gray-200">
                  <tr>
                    {['Title','Level','Topic','Last Updated','Actions'].map(h => (
                      <th key={h} className="text-left py-3 px-4 text-xs font-bold text-gray-500 uppercase tracking-wide">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {notes.map(n => (
                    <tr key={n.id} className="border-b border-gray-50 hover:bg-surface transition-colors">
                      <td className="py-3 px-4 font-semibold text-de-black text-xs max-w-xs truncate">{n.title}</td>
                      <td className="py-3 px-4"><Badge variant="gold" size="xs">{n.level}</Badge></td>
                      <td className="py-3 px-4 text-xs text-gray-500">{n.topic || '—'}</td>
                      <td className="py-3 px-4 text-xs text-gray-400">
                        {new Date(n.updated_at || n.created_at).toLocaleDateString('en-GB')}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex gap-2">
                          <Button size="sm" variant="secondary" onClick={() => openEdit(n)}>Edit</Button>
                          <Button size="sm" variant="danger"    onClick={() => setDeleteModal(n)}>Delete</Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {/* Create / Edit form */}
      {(tab === 'new' || tab === 'edit') && (
        <Card className="p-6" accent="red">
          <h3 className="text-base font-bold text-de-black mb-5">
            {tab === 'edit' ? `Editing: ${form.title || 'Note'}` : 'Create New Note'}
          </h3>
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="sm:col-span-2">
                <label className="block text-sm font-bold text-de-black mb-1.5">Title *</label>
                <input type="text" value={form.title} onChange={e => setF('title', e.target.value)}
                  placeholder="e.g. German Articles — der, die, das"
                  className="w-full px-4 py-2.5 text-sm border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:outline-none" />
              </div>
              <div>
                <label className="block text-sm font-bold text-de-black mb-1.5">Level</label>
                <select value={form.level} onChange={e => setF('level', e.target.value)}
                  className="w-full px-4 py-2.5 text-sm border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:outline-none bg-white">
                  {LEVELS.map(l => <option key={l} value={l}>Level {l}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-bold text-de-black mb-1.5">Topic / Category</label>
              <input type="text" value={form.topic} onChange={e => setF('topic', e.target.value)}
                placeholder="e.g. Grammar, Vocabulary, Pronunciation"
                className="w-full px-4 py-2.5 text-sm border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:outline-none" />
            </div>
            <div>
              <label className="block text-sm font-bold text-de-black mb-1.5">Content *</label>
              <RichEditor
                value={form.content}
                onChange={v => setF('content', v)}
                placeholder="Write your study notes here... Use the toolbar to format text."
              />
            </div>
            <div className="flex gap-3 justify-end pt-2">
              <Button variant="secondary" onClick={() => setTab('list')}>Cancel</Button>
              <Button variant="primary" loading={saving} onClick={handleSave}>
                {tab === 'edit' ? '✓ Save Changes' : '+ Publish Note'}
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Delete confirm */}
      <Modal isOpen={!!deleteModal} onClose={() => setDeleteModal(null)} title="Delete Note" size="sm">
        {deleteModal && (
          <div>
            <p className="text-sm text-de-text mb-4">
              Delete <strong>"{deleteModal.title}"</strong>? This cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <Button variant="secondary" onClick={() => setDeleteModal(null)}>Cancel</Button>
              <Button variant="danger" loading={deleting} onClick={handleDelete}>Delete</Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
